import { useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccountForm, type UnlockedAccount, type EntitlementCheck } from '@wiser/auth';
import { useAuth } from '@/context/AuthContext';
import {
  wiserAccountsEnabled,
  WISER_ONBOARD_URL,
  WISER_INVITE_CODE,
} from '@/wiserAuth';

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : '/api';

// Map @wiser/auth's design tokens (--wui-*) onto WiserWeb AI's dark theme so the shared form matches this
// app without pulling in the whole @wiser/ui stylesheet.
const wiserTheme: CSSProperties = {
  ['--wui-surface' as string]: '#18181b',
  ['--wui-text' as string]: '#f4f4f5',
  ['--wui-text-muted' as string]: '#a1a1aa',
  ['--wui-border' as string]: '#3f3f46',
  ['--wui-accent' as string]: '#6366f1',
  ['--wui-on-accent' as string]: '#ffffff',
  ['--wui-danger' as string]: '#f87171',
};

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, loginWithWiser } = useAuth();
  const navigate = useNavigate();

  // WiserWeb single-account login: the shared component does the content-blind wiser-accounts sign-in,
  // then we trade the session for our own JWT via loginWithWiser (POST /api/auth/wiser). Forward ONLY
  // account.token — never account.keys (password-derived keys are origin-bound, never cross origins).
  const onWiserAuthenticated = async (acct: UnlockedAccount) => {
    await loginWithWiser(acct.token);
    navigate('/');
  };

  // Entitlement seam. Pilot: every authenticated WiserWeb account is allowed into WiserWeb AI. The hook is
  // wired so a future shared plan service can gate here → { entitled: false, onboardUrl: WISER_ONBOARD_URL }.
  const checkEntitlement: EntitlementCheck = async () => ({ entitled: true });
  void WISER_ONBOARD_URL; // referenced by the seam above; kept for when gating turns on

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Invalid email or password');
      }

      const data = await response.json();
      login(data.token);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (wiserAccountsEnabled) {
    return (
      <div className="min-h-screen bg-base flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-brand to-brand-bright flex items-center justify-center">
              <span className="material-icons text-3xl text-white">auto_awesome</span>
            </div>
            <h1 className="text-2xl font-bold text-zinc-100">WiserWeb AI</h1>
            <p className="text-zinc-400 mt-2">Sign in with your WiserWeb account</p>
          </div>

          <div style={wiserTheme}>
            <AccountForm
              onAuthenticated={onWiserAuthenticated}
              entitlement={checkEntitlement}
              inviteCode={WISER_INVITE_CODE}
              client="wiserweb-ai"
              subtitle="One WiserWeb account for every app."
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-brand to-brand-bright flex items-center justify-center">
            <span className="material-icons text-3xl text-white">auto_awesome</span>
          </div>
          <h1 className="text-2xl font-bold text-zinc-100">WiserWeb AI</h1>
          <p className="text-zinc-400 mt-2">Sign in with your WiserWebCloud account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-zinc-300 mb-1">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-elevated border border-zinc-700 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
              placeholder="you@example.com"
              required
              disabled={isLoading}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-zinc-300 mb-1">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-elevated border border-zinc-700 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
              placeholder="Enter your password"
              required
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 px-4 rounded-lg bg-brand hover:bg-brand-bright text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                Signing in...
              </>
            ) : (
              <>
                <span className="material-icons text-xl">login</span>
                Sign In
              </>
            )}
          </button>
        </form>

        <p className="text-center text-zinc-500 text-sm mt-6">
          Don't have an account?{' '}
          <a
            href="https://wiserwebcloud.com/register"
            className="text-brand hover:text-brand-bright"
          >
            Sign up at WiserWebCloud
          </a>
        </p>
      </div>
    </div>
  );
}
